const DECIMAL_TEXT = /^\d+(\.\d+)?$/

/** An API decimal without trailing zeros, for editable inputs: "50.0000" → "50". */
export function trimDecimal(value?: string | null): string {
  if (!value) return ''
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value
}

function scaled(value: string): [bigint, number] {
  const [int, frac = ''] = value.trim().split('.')
  return [BigInt(int + frac), frac.length]
}

function unscaled(value: bigint, scale: number): string {
  if (scale === 0) return value.toString()
  const digits = value.toString().padStart(scale + 1, '0')
  return trimDecimal(`${digits.slice(0, -scale)}.${digits.slice(-scale)}`)
}

/** Exact sum of decimal strings (BigInt-scaled), for weight totals shown before the server recalculates. */
export function sumDecimals(values: string[]): string {
  const valid = values.map((value) => value.trim()).filter((value) => /^\d+(\.\d+)?$/.test(value))
  if (valid.length === 0) return '0'
  const scale = Math.max(...valid.map((value) => value.split('.')[1]?.length ?? 0))
  const total = valid.reduce((acc, value) => {
    const [int, frac = ''] = value.split('.')
    return acc + BigInt(int + frac.padEnd(scale, '0'))
  }, 0n)
  if (scale === 0) return total.toString()
  const digits = total.toString().padStart(scale + 1, '0')
  return `${digits.slice(0, -scale)}.${digits.slice(-scale)}`
}

/** Exact product of two decimal strings; '' when either is not a decimal. */
export function multiplyDecimals(left?: string, right?: string): string {
  if (!left || !right || !DECIMAL_TEXT.test(left.trim()) || !DECIMAL_TEXT.test(right.trim())) return ''
  const [a, aScale] = scaled(left)
  const [b, bScale] = scaled(right)
  return unscaled(a * b, aScale + bScale)
}

/** Decimal quotient rounded half-up to `places`; '' for missing input or a zero divisor. */
export function divideDecimals(left?: string, right?: string, places = 4): string {
  if (!left || !right || !DECIMAL_TEXT.test(left.trim()) || !DECIMAL_TEXT.test(right.trim())) return ''
  const [a, aScale] = scaled(left)
  const [b, bScale] = scaled(right)
  if (b === 0n) return ''
  const numerator = a * 10n ** BigInt(places + bScale) * 2n
  const denominator = b * 10n ** BigInt(aScale)
  return unscaled((numerator / denominator + 1n) / 2n, places)
}

/**
 * Procurement prices are stored per kg and shown and typed per tonne (owner
 * 2026-10-03: «Цена за 1 т»). Both are exact: the backend keeps 6 decimals
 * per kg, so a price per tonne may have up to 3.
 */
export function pricePerTonne(perKilogram?: string | null): string {
  return trimDecimal(multiplyDecimals(perKilogram ?? '', '1000'))
}

/** The price per kg of a price per tonne (exact; see pricePerTonne). */
export function pricePerKilogram(perTonne?: string | null): string {
  return trimDecimal(divideDecimals(perTonne ?? '', '1000', 9))
}
